import { type PageRect } from '@shared/board'
import type * as Pdfjs from 'pdfjs-dist'
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url'

// pdf.js, loaded on first use: the reader, clips on the board and preparing a
// new material all draw from the original PDF.

export type PdfDoc = Pdfjs.PDFDocumentProxy
export type PdfPage = Pdfjs.PDFPageProxy

let lib: Promise<typeof Pdfjs> | null = null
let legacy = false

/**
 * Load pdf.js's build for older browsers instead: for the page a headless
 * browser runs (src/pages/Prepare.tsx), whose browser version is not ours to
 * pick. Call before anything loads pdf.js.
 */
export function choosePdfjsLegacy() {
  legacy = true
}

export function pdfjs(): Promise<typeof Pdfjs> {
  lib ??= legacy
    ? Promise.all([
        import('pdfjs-dist/legacy/build/pdf.mjs'),
        import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url'),
      ]).then(([m, w]) => {
        m.GlobalWorkerOptions.workerSrc = w.default
        return m
      })
    : import('pdfjs-dist').then((m) => {
        m.GlobalWorkerOptions.workerSrc = workerUrl
        return m
      })
  return lib
}

const docs = new Map<string, Promise<PdfDoc>>()

/** The PDF at `url`, loaded once and shared (the reader and every clip of it). */
export function loadPdf(url: string): Promise<PdfDoc> {
  let p = docs.get(url)
  if (!p) {
    p = pdfjs().then((m) => m.getDocument({ url, withCredentials: true }).promise)
    p.catch(() => docs.delete(url))
    docs.set(url, p)
  }
  return p
}

/**
 * Draw `rect` of a page (fractions of the page) into `canvas` at `cssWidth`
 * CSS pixels wide, sharp on this screen. Returns a cancel function.
 */
export function drawRegion(
  doc: PdfDoc,
  pageNumber: number,
  rect: PageRect,
  canvas: HTMLCanvasElement,
  cssWidth: number,
): () => void {
  let task: Pdfjs.RenderTask | null = null
  let cancelled = false
  void doc.getPage(pageNumber).then((page) => {
    if (cancelled) return
    const base = page.getViewport({ scale: 1 })
    const ratio = window.devicePixelRatio || 1
    const scale = (cssWidth / (rect.w * base.width)) * ratio
    const vp = page.getViewport({ scale })
    canvas.width = Math.max(1, Math.round(rect.w * vp.width))
    canvas.height = Math.max(1, Math.round(rect.h * vp.height))
    task = page.render({
      canvas,
      viewport: vp,
      transform: [1, 0, 0, 1, -rect.x * vp.width, -rect.y * vp.height],
    })
    task.promise.catch(() => undefined)
  })
  return () => {
    cancelled = true
    task?.cancel()
  }
}
